import React from "react";

export type NavItem = { label: string; href: string };
export type Stat = { label: string; value: string; sub?: string };
export type Feature = {
    title: string;
    desc: string;
    icon: React.ReactNode;
    href?: string;
    tag?: string;
};
export type Plan = {
    name: string;
    price: string;
    period?: string;
    highlight?: boolean;
    desc: string;
    features: string[];
    ctaLabel: string;
    ctaHref: string;
};
export type FAQ = { q: string; a: string };
export type PreviewTab = {
    key: string;
    label: string;
    command: string;
    title: string;
    desc: string;
    transcript: { from: "user" | "bot"; text: string }[];
};

const Icon = ({ children }: { children: React.ReactNode }) => (
    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/90">
        {children}
    </span>
);

// Proste ikonki bez bibliotek
const Shield = (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 3l8 4v6c0 5-3.5 8-8 8s-8-3-8-8V7l8-4z" />
    </svg>
);

const Ticket = (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4 9a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 110 4v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2a2 2 0 110-4V9z" />
    </svg>
);

const Spark = (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 2l1.2 6.2L20 12l-6.8 3.8L12 22l-1.2-6.2L4 12l6.8-3.8L12 2z" />
    </svg>
);

const Chart = (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4 19V5" />
        <path d="M8 19V11" />
        <path d="M12 19V7" />
        <path d="M16 19V14" />
        <path d="M20 19V9" />
    </svg>
);

export const NAV: NavItem[] = [
    { label: "Funkcje", href: "#funkcje" },
    { label: "Podgląd", href: "#podglad" },
    { label: "Dla kogo", href: "#dla-kogo" },
    { label: "Cennik", href: "#cennik" },
    { label: "FAQ", href: "#faq" },
];

export const STATS: Stat[] = [
    { value: "15 000+", label: "Serwerów" },
    { value: "120 mln", label: "Akcji moderacji", sub: "w ostatnim kwartale" },
    { value: "50 ms", label: "Śr. czas reakcji" },
    { value: "4.9/5", label: "Ocena", sub: "7.5k opinii" },
];

export const FEATURES: Feature[] = [
    { title: "Anti-Spam & Ochrona", desc: "Wykrywanie raidów, linków, floodu i scamów.", icon: <Icon>{Shield}</Icon>, tag: "Bezpieczeństwo" },
    { title: "Automod & Logi", desc: "Reguły, kary, wyjątki oraz pełna historia działań.", icon: <Icon>{Spark}</Icon>, tag: "Kontrola" },
    { title: "Tickety & Pomoc", desc: "Formularze, kategorie, przypisania i transkrypcje.", icon: <Icon>{Ticket}</Icon>, tag: "Support" },
    { title: "Ekonomia & XP", desc: "Leveling, rankingi, nagrody i sklep serwerowy.", icon: <Icon>{Chart}</Icon>, tag: "Engagement" },
    { title: "Powitania & Role", desc: "Auto-roles, weryfikacja i onboarding użytkowników.", icon: <Icon>{Spark}</Icon>, tag: "Automatyzacje" },
    { title: "Zadania & Nagrody", desc: "Daily quests, streaki i eventy społeczności.", icon: <Icon>{Chart}</Icon>, tag: "Retention" },
];

export const PREVIEW_TABS: PreviewTab[] = [
    {
        key: "ticket",
        label: "/ticket",
        command: "/ticket create",
        title: "Twórz zgłoszenia w 2 kliknięcia",
        desc: "Użytkownicy wypełniają krótki formularz, a moderatorzy dostają uporządkowany wątek.",
        transcript: [
            { from: "user", text: "/ticket create" },
            { from: "bot", text: "✅ Utworzyłem zgłoszenie #pomoc-123. Wybierz kategorię: Płatności / Techniczne / Inne." },
            { from: "user", text: "Techniczne" },
            { from: "bot", text: "🧾 Opisz problem. Moderator zostanie powiadomiony." },
        ],
    },
    {
        key: "ban",
        label: "/ban",
        command: "/ban @user spam",
        title: "Szybka moderacja i audyt",
        desc: "Akcje są logowane, a kary można cofnąć lub edytować z dashboardu.",
        transcript: [
            { from: "user", text: "/ban @spammer spam" },
            { from: "bot", text: "🔨 Zbanowano @spammer. Powód: spam. (ID akcji: 98342)" },
            { from: "bot", text: "📌 Zapisano w logach moderacji • #mod-log" },
        ],
    },
    {
        key: "autorole",
        label: "/autorole",
        command: "/autorole set @Member",
        title: "Automatyczne role i onboarding",
        desc: "Nowi użytkownicy dostają role, reguły i powitania — bez ręcznej pracy.",
        transcript: [
            { from: "user", text: "/autorole set @Member" },
            { from: "bot", text: "✅ Ustawiono AutoRole: @Member dla nowych użytkowników." },
            { from: "bot", text: "ℹ️ Wskazówka: połącz z weryfikacją, aby ograniczyć boty." },
        ],
    },
    {
        key: "level",
        label: "/level",
        command: "/level rank",
        title: "Leveling, rankingi i nagrody",
        desc: "XP za aktywność + nagrody na poziomach. Idealne do rozkręcania community.",
        transcript: [
            { from: "user", text: "/level rank" },
            { from: "bot", text: "🏅 Twój poziom: 12 • XP: 3 240 • Do kolejnego: 260 XP" },
            { from: "bot", text: "🥇 Top 3: @A, @B, @C" },
        ],
    },
];

export const PLANS: Plan[] = [
    {
        name: "Free",
        price: "0 zł",
        period: "/ mies.",
        desc: "Start dla małych serwerów i testów.",
        features: ["Podstawowa moderacja", "Proste logi", "1 formularz ticketów", "Podstawowy leveling"],
        ctaLabel: "Dodaj bota",
        ctaHref: "https://discord.com/oauth2/authorize",
    },
    {
        name: "Pro",
        price: "19 zł",
        period: "/ mies.",
        highlight: true,
        desc: "Najlepszy balans funkcji i ceny.",
        features: ["Zaawansowany Automod", "Tickety z kategoriami", "Eksport/transkrypcje", "Zadania i nagrody", "Priorytet support"],
        ctaLabel: "Wybierz plan",
        ctaHref: "#",
    },
    {
        name: "Enterprise",
        price: "Indywidualnie",
        desc: "Dla dużych community i firm.",
        features: ["SLA + monitoring", "White-label", "Dedykowane integracje", "Wdrożenie i szkolenie"],
        ctaLabel: "Skontaktuj się",
        ctaHref: "#",
    },
];

export const FAQS: FAQ[] = [
    { q: "Jakie uprawnienia są wymagane?", a: "Minimalne: Manage Messages i View Audit Log. Dodatkowe tylko dla wybranych modułów (np. tickety)." },
    { q: "Czy mogę wyłączyć moduły?", a: "Tak — każdy moduł włączasz/wyłączasz per serwer w dashboardzie." },
    { q: "Czy bot przechowuje dane prywatne?", a: "Tylko to, co potrzebne do działania funkcji (np. ustawienia serwera, logi akcji). Bez czytania DM." },
    { q: "Czy działa na wielu serwerach?", a: "Tak — każdy serwer ma osobną konfigurację i osobne moduły." },
    { q: "Jak szybko wystartuję?", a: "Dodaj bota, wybierz preset i gotowe. Możesz dopracować ustawienia później." },
];
